import { ColumnDef, CellContext } from "@tanstack/react-table";
import CopyToClipboard from "react-copy-to-clipboard";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import classNames from "classnames";
import BettingTeamName from "../BettingTeamName";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";

const isGoodTeam = (asiaHandicap = "") => {
  return asiaHandicap.includes("-");
};

const MatchStatusLabel = ({
  status,
  remark,
}: {
  status?: string;
  remark?: string;
}) => {
  const { t } = useTranslation();
  if (status === "draw" && remark) return <>{t("Refund")}</>;
  return <>{status ? t(status, { defaultValue: status }) : status}</>;
};

const StatusLabel = ({ value }: { value: string }) => {
  const { t } = useTranslation();
  return <>{t(value, { defaultValue: value })}</>;
};

const DisplayNumberCell = (data: CellContext<any, unknown>) => {
  const value = data.getValue<number>() || 0;
  return (
    <div className="text-right">
      <b
        className={classNames({
          "text-red-500": value < 0,
        })}>
        {Number(Number(value).toFixed(2)).toLocaleString("en-US")}
      </b>
    </div>
  );
};

const DisplayDateCell = (data: CellContext<any, unknown>) => {
  const createDate = data.getValue<string>();
  return (
    <div className="text-center text-xs">
      {createDate ? dayjs(createDate).format("DD-MM-YYYY HH:mm:ss") : "-"}
    </div>
  );
};

type PeriodScore = {
  matchStatusCode?: number;
  homeScore?: number;
  awayScore?: number;
};

const hasCompleteScore = (
  score?: { homeScore?: number | null; awayScore?: number | null } | null,
): score is { homeScore: number; awayScore: number } => {
  return score != null && score.homeScore != null && score.awayScore != null;
};

const SET_PERIOD_CODES = [8, 9, 10, 11, 12] as const;
const BASKETBALL_PERIOD_CODES = [13, 14, 15, 16] as const;
const ICEHOCKEY_PERIOD_CODES = [1, 2, 3] as const;
const BASEBALL_PERIOD_CODES = [531, 532, 533, 534, 535, 536, 537, 538, 539] as const;

const formatPeriodResult = (
  periodScores: PeriodScore[] = [],
  periodCodes: readonly number[],
  prefix: "S" | "Q" | "P" | "In",
) => {
  return periodCodes
    .map((matchStatusCode, index) => {
      const periodScore = periodScores.find(
        (score) => score?.matchStatusCode === matchStatusCode,
      );

      if (
        !periodScore ||
        periodScore.homeScore == null ||
        periodScore.awayScore == null
      ) {
        return "";
      }

      return `${prefix}${index + 1}[${periodScore.homeScore}:${periodScore.awayScore}]`;
    })
    .filter(Boolean)
    .join(" ");
};

export const COLUMNS: ColumnDef<any>[] = [
  {
    accessorKey: "no",
    header: "No",
    cell: (data) => {
      return (
        <div className="text-center">
          {((data as any)?.page - 1) * (data as any)?.limit +
            (data.row as any).index +
            1}
        </div>
      );
    },
  },
  {
    accessorKey: "sport",
    header: "Matches",
    cell: (data) => {
      const matches = data.getValue<any[]>();

      return (
        <>
          <div className="text-xs">
            Bet Id:{" "}
            <CopyToClipboard
              text={data.row.original.betId}
              onCopy={() => {
                toast.success(
                  <div>
                    <div className="font-medium">Copied</div>
                    <div className="text-sm break-all">
                      {data.row.original.betId}
                    </div>
                  </div>,
                  { style: { fontSize: "14px" } },
                );
              }}>
              <button
                className="italic font-semibold flex gap-x-1"
                style={{ fontSize: 12 }}>
                {data.row.original.betId}
                <ContentCopyIcon className="!w-4 !h-4" />
              </button>
            </CopyToClipboard>
          </div>
          <div className="text-xs">
            Ref Id:{" "}
            <CopyToClipboard
              text={data.row.original.refId}
              onCopy={() => {
                toast.success(
                  <div>
                    <div className="font-medium">Copied</div>
                    <div className="text-sm break-all">
                      {data.row.original.refId}
                    </div>
                  </div>,
                  { style: { fontSize: "14px" } },
                );
              }}>
              <button
                className="italic font-semibold flex text-left text-xs gap-x-1 break-all"
                style={{ fontSize: 10 }}>
                {data.row.original.refId}
                <ContentCopyIcon className="!w-4 !h-4" />
              </button>
            </CopyToClipboard>
          </div>
          <div>
            <div className="flex text-xs bg-white">
              <div className="border-b py-1 font-bold border-black w-6 text-center">
                No.
              </div>
              <div className="border-b py-1 font-bold border-l border-black flex-1 text-center">
                Matches
              </div>
            </div>
            {matches?.map((match, index) => {
              let isHomeTeamGood = false;
              let isAwayTeamGood = false;

              if (match.value !== "0" || match.option === 3) {
                if (match.option === 0 && isGoodTeam(match.value)) {
                  isHomeTeamGood = true;
                }

                if (match.option === 0 && !isGoodTeam(match.value)) {
                  isAwayTeamGood = true;
                }

                if (match.option === 1 && isGoodTeam(match.value)) {
                  isAwayTeamGood = true;
                }

                if (match.option === 1 && !isGoodTeam(match.value)) {
                  isHomeTeamGood = true;
                }
              }

              let result = "";
              if (
                match.sportType === "VOLLEYBALL" ||
                match.sportType === "TABLETENNIS" ||
                match.sportType === "BADMINTON" ||
                match.sportType === "TENNIS"
              ) {
                result = formatPeriodResult(
                  match.result?.periodScores ?? [],
                  SET_PERIOD_CODES,
                  "S",
                );
              } else if (match.sportType === "BASKETBALL") {
                result = formatPeriodResult(
                  match.result?.periodScores ?? [],
                  BASKETBALL_PERIOD_CODES,
                  "Q",
                );
              } else if (match.sportType === "ICEHOCKEY") {
                result = formatPeriodResult(
                  match.result?.periodScores ?? [],
                  ICEHOCKEY_PERIOD_CODES,
                  "P",
                );
              } else if (match.sportType === "BASEBALL") {
                result = formatPeriodResult(
                  match.result?.periodScores ?? [],
                  BASEBALL_PERIOD_CODES,
                  "In",
                );
              } else if (match.sportType === "FOOTBALL") {
                const periodScores: (PeriodScore | null)[] =
                  match.result?.periodScores ?? [];
                const firstHalf = periodScores.find(
                  (score) => score?.matchStatusCode === 6,
                );
                const secondHalf = periodScores.find(
                  (score) => score?.matchStatusCode === 7,
                );
                const penalties = periodScores.find(
                  (score) => score?.matchStatusCode === 50,
                );
                const showFinalScore =
                  match.status !== "running" &&
                  match.status !== "waiting" &&
                  match.status !== "cancelled";
                const HT = hasCompleteScore(firstHalf)
                  ? `HT [${firstHalf.homeScore}:${firstHalf.awayScore}]`
                  : "";
                const FT =
                  showFinalScore &&
                  hasCompleteScore(firstHalf) &&
                  hasCompleteScore(secondHalf)
                    ? `FT [${firstHalf.homeScore + secondHalf.homeScore}:${firstHalf.awayScore + secondHalf.awayScore}]`
                    : "";
                const PEN = showFinalScore && hasCompleteScore(penalties)
                  ? `PEN [${penalties.homeScore}:${penalties.awayScore}]`
                  : "";
                const isFirstHalfCornerWithoutPeriods =
                  match.betType === "1h/cr" &&
                  match.result?.periodScores?.length === 0;

                if (
                  isFirstHalfCornerWithoutPeriods &&
                  hasCompleteScore(match.result)
                ) {
                  result = `HT [${match.result.homeScore}:${match.result.awayScore}]`;
                } else {
                  result = [HT, FT, PEN].filter(Boolean).join(" | ");
                }
              } else {
                const HT =
                  match.result?.periodScores?.[0] &&
                  `HT [${match.result?.periodScores?.[0]?.homeScore}:${match.result?.periodScores?.[0]?.awayScore}]`;
                let FT =
                  match.status !== "running" &&
                  match.status !== "waiting" &&
                  match.status !== "cancelled" &&
                  `FT [${match.result.homeScore}:${match.result.awayScore}]`;

                if (
                  match.betType === "1h/cr" &&
                  match.result.periodScores.length === 0
                ) {
                  FT = `HT [${match.result.homeScore}:${match.result.awayScore}]`;
                }

                result = [HT, FT].filter(Boolean).join(" | ");
              }

              return (
                <div
                  key={match._id}
                  className="odd:bg-blue-gray-100 even:bg-white">
                  <div className="flex">
                    <div className="">
                      <div className="bg-yellow-200 mt-2 w-6 text-center">
                        {index + 1}.
                      </div>
                    </div>
                    <div className="text-xs border-l p-2 border-black flex-1">
                      <div
                        className={classNames(
                          "italic font-semibold bg-blue-50",
                          {
                            "text-red-500": isHomeTeamGood,
                          },
                        )}>
                        {match.matchName.en.h}
                      </div>
                      <div
                        className={classNames(
                          "italic font-semibold bg-blue-50",
                          {
                            "text-red-500": isAwayTeamGood,
                          },
                        )}>
                        {match.matchName.en.a}
                      </div>
                      {match.table === "live" && (
                        <div>
                          Score at that time: <b>[{match.score}]</b>
                        </div>
                      )}
                      <div>
                        Type:{" "}
                        <b
                          className={classNames({
                            "text-red-500": match.table === "live",
                            "text-green-800": match.table === "today",
                          })}>
                          {match.table}{" "}
                          {match.table === "live" ? `(${match.info?.lt})` : ""}
                        </b>
                      </div>
                      <div>
                        Sport: <b>{match.sportType}</b>
                      </div>
                      <div>
                        Market:{" "}
                        <b>
                          {`${match.betType}`.toUpperCase()}{" "}
                          {!(
                            match.betType === "oe" ||
                            match.betType === "cr" ||
                            match.betType === "1h/cr" ||
                            match.betType === "2h/cr"
                          ) && `[${match.value}]`}
                        </b>
                      </div>
                      <div>
                        Price:{" "}
                        <b
                          className={classNames({
                            "text-red-500": match.price < 0,
                          })}>
                          {match.price}
                        </b>
                      </div>
                      <div className="flex">
                        <span>Bet side: </span>
                        <BettingTeamName
                          outcomeId={match.outcomeID}
                          option={match.option}
                          isHomeGoodTeam={isHomeTeamGood}
                          isAwayGoodTeam={isAwayTeamGood}
                          home={match.matchName.en.h}
                          away={match.matchName.en.a}
                          betPriceType={match.betType}
                        />
                      </div>

                      <div>
                        Status:{" "}
                        <b
                          className={classNames({
                            "text-red-500": match.status?.includes("lose"),
                            "text-green-800": match.status?.includes("win"),
                            "text-blue-800": match.status === "draw",
                          })}>
                          <MatchStatusLabel
                            status={match.status}
                            remark={match?.remark}
                          />
                          <br />
                          {result}
                        </b>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      );
    },
  },
  {
    accessorKey: "type",
    header: "Type",
    cell: (data) => {
      const type = data.getValue<string>();
      return <div className="text-center">{type}</div>;
    },
  },
  // {
  //   accessorKey: "realBetAmt",
  //   header: "Real bet amount",
  //   cell: DisplayNumberCell,
  // },
  {
    accessorKey: "betAmt",
    header: "Bet amount",
    cell: DisplayNumberCell,
  },
  {
    accessorKey: "status",
    header: "Status",
    cell: (data) => {
      const status = data.getValue<string>();
      const isCancelled = status.toLowerCase() === "cancelled";
      return (
        <div className="text-center">
          <b className={isCancelled ? "text-red-800" : ""}>
            <StatusLabel value={status.toLowerCase()} />
          </b>
          <br />

          {isCancelled &&
            (data.row.original?.remark === "EVENT_ABANDONED" ?
              "Competition was cancelled. การแข่งขันถูกยกเลิก"
            : "")}
        </div>
      );
    },
  },
  {
    accessorKey: "winLose",
    header: "Win lose",
    cell: DisplayNumberCell,
  },
  {
    accessorKey: "createDate",
    header: "Created at",
    cell: DisplayDateCell,
  },
  {
    accessorKey: "updateDate",
    header: "Updated at",
    cell: DisplayDateCell,
  },
  {
    accessorKey: "settleDate",
    header: "Settled at",
    cell: DisplayDateCell,
  },
  // {
  //   accessorKey: "username",
  //   header: "By",
  //   cell: (data) => {
  //     const username = data.getValue<string>();
  //     return <div className="text-center">{username}</div>;
  //   },
  // },
];
